import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

// Get current season based on month
const getCurrentSeason = () => {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 7) return 'summer';
  if (month >= 8 && month <= 10) return 'fall';
  return 'winter';
};

const seasonalThemes = {
  spring: {
    primary: '#2E7D32',
    secondary: '#F06292',
    background: '#F1F8E9',
    accent: '#A5D6A7',
    overlay: 'rgba(255, 255, 255, 0.6)',
    greeting: 'Spring Blessings',
    emoji: '🌸',
    particles: 'petals'
  },
  summer: {
    primary: '#F57F17',
    secondary: '#0288D1',
    background: '#FFFDE7',
    accent: '#FFECB3',
    overlay: 'rgba(255, 252, 220, 0.6)',
    greeting: 'Summer Joy',
    emoji: '☀️',
    particles: 'sunrays'
  },
  fall: {
    primary: '#D84315',
    secondary: '#5D4037',
    background: '#FBE9E7',
    accent: '#FFAB91',
    overlay: 'rgba(255, 240, 230, 0.6)',
    greeting: 'Autumn Grace',
    emoji: '🍂',
    particles: 'leaves'
  },
  winter: {
    primary: '#0277BD',
    secondary: '#455A64',
    background: '#E1F5FE',
    accent: '#81D4FA',
    overlay: 'rgba(225, 245, 254, 0.6)',
    greeting: 'Winter Peace',
    emoji: '❄️',
    particles: 'snow'
  }
};

const allSeasonKeys = Object.keys(seasonalThemes);

const SeasonContext = createContext();

export const useSeason = () => {
  const context = useContext(SeasonContext);
  if (!context) {
    throw new Error('useSeason must be used within a SeasonProvider');
  }
  return context;
};

export const SeasonProvider = ({ children }) => {
  const [season, setSeason] = useState(getCurrentSeason());
  const [autoMode, setAutoMode] = useState(true);

  useEffect(() => {
    // Apply CSS variables to root
    const theme = seasonalThemes[season];
    const root = document.documentElement;
    
    root.style.setProperty('--seasonal-primary', theme.primary);
    root.style.setProperty('--seasonal-secondary', theme.secondary);
    root.style.setProperty('--seasonal-background', theme.background);
    root.style.setProperty('--seasonal-accent', theme.accent);
    root.style.setProperty('--seasonal-overlay', theme.overlay);
  }, [season]);

  useEffect(() => {
    if (autoMode) {
      setSeason(getCurrentSeason());
    }
  }, [autoMode]);

  const changeSeason = useCallback((newSeason) => {
    setAutoMode(false);
    setSeason(newSeason);
  }, []);

  const resetToAuto = useCallback(() => {
    setAutoMode(true);
    setSeason(getCurrentSeason());
  }, []);

  const value = useMemo(() => ({
    season,
    theme: seasonalThemes[season],
    changeSeason,
    resetToAuto,
    autoMode,
    allSeasons: allSeasonKeys
  }), [season, changeSeason, resetToAuto, autoMode]);

  return (
    <SeasonContext.Provider value={value}>
      {children}
    </SeasonContext.Provider>
  );
};

export default SeasonContext;
